import { Component, Input } from '@angular/core'
import { PageContext, Redirector4SaleConfig } from 'src/app/service/redirector.service'
import { PurchaseDomainFaqQuestions } from 'src/static/content/purchase-domain.faq.questions'
import { NgClass, NgIf, NgOptimizedImage } from '@angular/common'
import { SafePipe } from 'src/app/pipes/safe.pipe'
import { PriceComponent } from 'src/app/elements/price/price.component'
import { TaxNoteComponent } from 'src/app/elements/tax-note/tax-note.component'
import { IconComponent } from 'src/app/elements/icon/icon.component'
import { ContentBlockComponent } from 'src/app/elements/content-block/content-block.component'
import { I18nSelectorComponent } from 'src/app/elements/i18n-selector/i18n-selector.component'
import { AccordionComponent } from 'src/app/elements/accordion/accordion.component'
import { ButtonComponent } from 'src/app/elements/button/button.component'
import { ContactformComponent } from 'src/app/elements/contactform/contactform.component'
import { RouterLink } from '@angular/router'
import { UserTokenPipe } from 'src/app/pipes/promo-link.pipe'

@Component({
  selector: 'app-forsale-expert',
  templateUrl: './forsale-expert.component.html',
  styleUrl: './forsale-expert.component.sass',
  imports: [
    NgClass,
    SafePipe,
    PriceComponent,
    TaxNoteComponent,
    IconComponent,
    ContentBlockComponent,
    I18nSelectorComponent,
    AccordionComponent,
    NgOptimizedImage,
    ButtonComponent,
    NgIf,
    ContactformComponent,
    RouterLink,
    UserTokenPipe,
  ],
})
export class ForsaleExpertComponent {
  @Input() context!: PageContext<Redirector4SaleConfig>

  protected readonly year = new Date().getFullYear()
  protected readonly PurchaseDomainFaqQuestions = PurchaseDomainFaqQuestions
  protected showForm = false

  protected usps = [{
    icon: 'lock',
    text: $localize`Sicher`,
  }, {
    icon: 'thumb_up_alt',
    text: $localize`Einfach`,
  }, {
    icon: 'watch_later',
    text: $localize`Schnell`,
  }]

  protected faqGroups = [{
    'title': $localize`Abwicklung des Domainkaufs`,
    'icon': 'shopping_cart',
    'questions': [{
      'head': $localize`Wie kauft man eine Domain?`,
      'body': `
        <p>${$localize`Ein Domainkauf funktioniert in nur wenigen Schritten einfach und sicher. Allgemein gehen Sie folgendermaßen vor:`}</p>
        <ul>
          <li>${$localize`Sie übermitteln Ihre Daten mit Preisvorstellung und klicken dann auf "Anfrage senden".`}</li> 
          <li>${$localize`Nach Einigung erhalten Sie eine ordnungsgemäße Rechnung und einen optionalen Kaufvertrag.`}</li>  
          <li>${$localize`Zahlen Sie bequem und sicher per IBAN-Überweisung oder Paypal.`}</li> 
          <li>${$localize`Nach Zahlungseingang wird die Domain an Ihren Provider per Transfer übertragen (via Auth-Code).`}</li>  
          <li>${$localize`Sie können Ihre neue Domain für ein neues Projekt nutzen oder auf Ihre bestehende Homepage weiterleiten.`}</li> 
        </ul>
      `,
    }, {
      'head': $localize`Wie bezahlt man eine Domain?`,
      'body': `
        <p>${$localize`Nach einer Einigung wird der Inhaber Ihnen die Details der Zahlung mitteilen. Der Inhaber wird Ihnen dann die SEPA Bankdetails mitteilen und auf Wunsch auch Paypal oder weitere Zahlungsmethoden anbieten.`}</p>
        <p>${$localize`Bitte geben Sie bei der Überweisung immer den Domainnamen und die Rechnungsnummer an.`}</p>
      `,
    }, {
      'head': $localize`Bekommt man eine Rechnung?`,
      'body': `
        <p>${$localize`Ja, der Verkäufer wird Ihnen eine ordnungsgemäße Rechnung senden. Bei größeren Kaufpreisen bekommen Sie auf Wunsch auch einen zusätzlichen Kaufvertrag.`}</p>
      `,
    }],
  }, {
    'title': $localize`Kosten der Domain`,
    'icon': 'point_of_sale',
    'questions': [{
      'head': $localize`Was kostet eine Domain?`,
      'body': `
        <p>${$localize`Die Preise für Domains können generell sehr unterschiedlich sein. Sofern ein Preis abgegeben wurde, können Sie das Angebot annehmen und diese Domain sofort zum Festpreis kaufen.',`}</p>
        <p>${$localize`Wissenswertes zu den Preisen von Domains:',`}</p>
        <ul>
          <li>${$localize`Preise für Domains beginnen ab ca. 200 € netto.`}</li>
          <li>${$localize`Die meisten Domains werden zwischen 500 € und 2.500 € verkauft.`}</li>
          <li>${$localize`Besonders gute Domains kosten ein Vermögen.`}</li>
          <li>${$localize`Sollte kein Festpreis angegeben sein, können Sie anonym und schnell mit dem Verkäufer verhandeln.`}</li>
          <li>${$localize`Zur Orientierung sollten sie Preise von vergleichbaren, bereits verkauften, Domains recherchieren.`}</li>
        </ul>
      `,
    }, {
      'head': $localize`Fallen zusätzliche Gebühren an?`,
      'body': `
        <p>${$localize`Nein, für den Käufer fallen grundsätzlich keine zusätzlichen Gebühren an.`}</p>
      `,
    }, {
      'head': $localize`Sind Preise mit Steuern angegeben?`,
      'body': `
        <p>${$localize`Der Domainpreis ist immer brutto, also inklusive Umsatzsteuer ausgewiesen. Achten Sie immer darauf, dass Ihre Steuerdaten richtig sind. Geben Sie unbedingt Ihre Umsatzsteuer-Identifikationsnummer an, sofern Sie als Firma auftreten.`}</p>
      `,
    }, {
      'head': $localize`Was ist eine Umsatzsteuer-Identifikationsnummer?`,
      'body': `
        <p>${$localize`Eine sogenannte Umsatzsteuer-Identifikationsnummer (USt-IdNr., UID, VAT-ID) dient europäischen Firma bei der Verrechnung der Umsatzsteuer in den EU-Ländern.`}</p>
        <p>${$localize`Käufer aus der EU mit einer gültigen Umsatzsteuer-Identifikationsnummer, die nicht aus dem Inland kommen, wird nur der Nettobetrag in Rechnung gestellt.`}</p>
        <p>${$localize`Achten Sie auf eine korrekte Umsatzsteuer-Identifikationsnummer.`}</p>
      `,
    }],
  }, {
    'title': $localize`Transfer der Domain`,
    'icon': 'move_down',
    'questions': [{
      'head': $localize`Wie funktioniert ein Domain-Transfer?`,
      'body': `
        <p>${$localize`Der Transfer bzw. Inhaberwechsel einer Domain verläuft in der Regel folgendermaßen:`}</p>
        <ul>
          <li>${$localize`Der Verkäufer übermittelt nach der Kaufpreiszahlung einen sogenannten Auth-Code an den Käufer.`}</li>
          <li>${$localize`Mit diesem Code kann der Käufer die Domain von seinem Provider übernehmen lassen.`}</li>
          <li>${$localize`Im Anschluss aktualisiert der Provider die Inhaberdaten und macht damit den Käufer zum neuen Inhaber.`}</li>
        </ul>
      `,
    }, {
      'head': $localize`Wie wird man Inhaber der Domain?`,
      'body': `
        <p>${$localize`Als Käufer wird man nach dem Transfer automatisch neuer Inhaber der Domain. Beim Transfer der Domain wird mit einem Auth-Code der Datensatz der Domain aktualisiert. Der neue Provider wird Sie dabei zum neuen Inhaber der Domain machen.`}</p>
      `,
    }, {
      'head': $localize`Welche Sicherheiten hat man?`,
      'body': `
        <p>${$localize`Der Verkäufer der Domain ist sehr erfahren in der Domainvermittlung und besitzt mehrere Domains. Nach Zahlungseingang wird die Domain auf den Käufer sofort per Transfer übertragen.`}</p>
      `,
    }],
  }, {
    'title': $localize`Verwendung der Domain`,
    'icon': 'info',
    'questions': [{
      'head': $localize`Wie kann die Domain verwendet werden?`,
      'body': `
        <p>${$localize`Sie können eine Domain für viele Arten verwenden, beispielsweise:`}</p>
        <ul>
          <li>${$localize`Für eine eigene Webseite.`}</li>
          <li>${$localize`Für einen Internetshop.`}</li>
          <li>${$localize`Als Landingpage für ein Produkt.`}</li>
          <li>${$localize`Als Weiterleitung auf ein bereits bestehendes Projekt.`}</li>
          <li>${$localize`Zur Messung spezieller Marketingmaßnahmen.`}</li>
          <li>${$localize`Als Wertanlage, denn ein Gewinn kann natürlich auch weiterverkauft werden.`}</li>
        </ul>
      `,
    }, {
      'head': $localize`Warum diese Domain?`,
      'body': `
        <p>${$localize`Gute Domains sind nur selten auf dem Markt verfügbar. Falls Sie eine passende Domain gefunden haben, schlagen Sie zu, bevor es Ihre Konkurrenz tut.`}</p>
      `,
    }, {
      'head': $localize`Warum eine gute Domain kaufen?`,
      'body': `
        <p>${$localize`Folgende Gründe sprechen für den Kauf von guten Domains:`}</p>
        <ul>
          <li>${$localize`Eine Wertanlage für die Firma.`}</li>
          <li>${$localize`Kunden merken sich eine gute Domains als Adresse.`}</li>
          <li>${$localize`Niedrigere Marketingkosten und mehr Unabhängigkeit von Google und Co.`}</li>
          <li>${$localize`Höhere Sichtbarkeit durch eine schöne Domain.`}</li>
          <li>${$localize`Eine Einzigartigkeit und bessere Merkbarkeit.`}</li>
          <li>${$localize`Mehr Kundenvertrauen durch einen seriösen Auftritt.`}</li>
        </ul>
      `,
    }],
  }]
  protected currGroup = this.faqGroups[0]
}
